import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import { doUserEnrollment, doUserUnenrollment } from "sj-ui-toolkit/src/api";
import { useEnrollments, useUserAssignments } from "sj-ui-toolkit/src/api/hooks";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { getPathByCollectionKey } from "sj-ui-toolkit/src/api/user";
import { isEmpty } from "sj-ui-toolkit/src/utils";
import { transformPathData } from "sj-ui-toolkit/src/atlas/utils";
import { INTERNAL_ROUTES } from "sj-ui-toolkit/src/constants/routes";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { readBreadcrumbState } from "sj-ui-toolkit/src/helpers/breadcrumbStorage";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { PageBreadcrumb } from "sj-ui-toolkit/src/atlas/components";

import { PathDetails } from "./PathDetails";
import { LessonsContainer } from "./LessonsContainer";
import EnrollmentPromptModal from "./EnrollmentPromptModal";
import { useLevelContext } from "sj-ui-toolkit/src/atlas/contexts/Level/LevelContextProvider";

const LevelIdBeltMap = {
  1: "white",
  2: "yellow",
  3: "green",
};

const Path = ({ pathType = "Path" }) => {
  const { pathId } = useParams();
  const [pathData, setPathData] = useState({
    pathDetails: {},
    remainingLessons: [],
    completedLessons: [],
  });
  const { navigateTo } = useSmartNavigation();
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState("loading");
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [pendingLessonRoute, setPendingLessonRoute] = useState(null);
  const [currentLevelEnrollment, setCurrentLevelEnrollment] = useState();
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [enrollmentModalDismissed, setEnrollmentModalDismissed] = useState(false);

  const toast = useRef(null);
  const { data: currentPaths = [], refetch: refetchEnrollments } = useEnrollments();
  const { levels, refetchLevels, refetchQuests } = useLevelContext();
  const { data: assignments = [] } = useUserAssignments();

  const { settings } = useSettingsContext();
  const { strictAssignment } = settings || {};

  const hasOutstandingStrictAssignment = useMemo(() => {
    if (!strictAssignment || !assignments?.length) return false;
    return assignments.some(assignment => assignment.status !== "passed");
  }, [assignments, strictAssignment]);

  const isAssignmentPath = useMemo(() => {
    if (!pathData?.pathDetails?.id || !assignments?.length) return false;
    return assignments.some(a => a.status !== "passed" && a.roleId == pathData.pathDetails.id);
  }, [assignments, pathData?.pathDetails?.id]);

  const isInactivePath = !pathData?.pathDetails?.activated;
  const isLevelLocked =
    !pathData?.pathDetails?.enrolledInLevel && !pathData?.pathDetails?.enrolledInPath;

  const disabledEnrollButton =
    (hasOutstandingStrictAssignment && (!isAssignmentPath || isEnrolled === true)) ||
    pathData?.pathDetails?.status === "passed" ||
    (pathType === "Path" && (isInactivePath || isLevelLocked));

  const isReassignedAndNotEnrolled = pathData?.pathDetails?.reassigned && isEnrolled === false;

  const disableLessons =
    (hasOutstandingStrictAssignment && !isAssignmentPath) || isReassignedAndNotEnrolled;

  const pathTitle = pathData?.pathDetails?.title;

  useBreadcrumb(pathTitle ? { label: pathTitle, route: `/path/${pathId}`, display: "page" } : null);
  const isFromCatalog = () => readBreadcrumbState()?.root?.route === "/catalog";

  useEffect(() => {
    if (!isEmpty(currentPaths) && !isEmpty(levels) && !isEmpty(pathData)) {
      const currentLevelId = pathData.pathDetails?.levelId;

      if (!currentLevelId) return;

      const currentEnrollmentInLevel = currentPaths?.find(
        p => p.levelRank === LevelIdBeltMap[currentLevelId]
      );

      if (currentEnrollmentInLevel) {
        const levelName = levels?.find(
          level => level.rankName === currentEnrollmentInLevel.levelRank
        )?.name;
        setCurrentLevelEnrollment({ ...currentEnrollmentInLevel, levelName });
      }
    }
  }, [currentPaths, levels, pathData]);

  useEffect(() => {
    // If user is not enrolled but has completed lessons, show enrollment modal
    if (isEnrolled === false && !isFromCatalog() && pathData?.completedLessons?.length) {
      setShowEnrollmentModal(true);
    }
    // If path is reassigned and user is not enrolled, show enrollment modal
    if (isEnrolled === false && pathData?.pathDetails?.reassigned) {
      setShowEnrollmentModal(true);
    }
  }, [isEnrolled, pathData?.pathDetails?.reassigned]);

  useEffect(() => {
    if (isEnrolled === "loading" && pathData?.pathDetails?.id && currentPaths) {
      const enrolled = currentPaths.some(
        currentPath => currentPath.roleId == pathData.pathDetails.id
      ); // double equals intentional
      setIsEnrolled(enrolled);
    }
  }, [currentPaths, pathData]);

  useEffect(() => {
    const fetchPath = async () => {
      if (!pathId) return;
      try {
        const response = await getPathByCollectionKey(pathId);
        const formattedPath = transformPathData(response);
        setPathData(formattedPath);
      } catch (error) {
        console.error("Error fetching path:", error);
        setNoResultsFound(true);
      }
    };

    fetchPath();
  }, [pathId]);

  const handleEnroll = async overrideOtherPaths => {
    const pathTypeName = pathType === "Quest" ? "quest" : "path";
    setIsEnrolling(true);
    try {
      await doUserEnrollment(pathData.pathDetails.id, overrideOtherPaths);

      setPathData(prev => ({
        ...prev,
        pathDetails: { ...prev.pathDetails, enrolledInPath: true },
      }));
      setIsEnrolled(true);
      refetchEnrollments();
      if (pathType === "Quest") {
        refetchQuests();
      } else {
        refetchLevels();
      }

      toast.current?.show({
        severity: "success",
        summary: "Enrolled",
        detail: `You have successfully enrolled in this ${pathTypeName}.`,
        life: 3000,
      });

      // If there's a pending lesson, navigate to it after enrollment
      if (pendingLessonRoute) {
        navigateTo(pendingLessonRoute);
        setPendingLessonRoute(null);
      }
    } catch (_error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to enroll in ${pathTypeName}. Please try again.`,
        life: 3000,
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleUnenroll = async () => {
    const pathTypeName = pathType === "Quest" ? "quest" : "path";
    try {
      const currentPath = currentPaths.find(
        currentPath => currentPath.roleId == pathData.pathDetails.id
      );
      if (!currentPath) return;

      await doUserUnenrollment(currentPath.roleId);

      setPathData(prev => ({
        ...prev,
        pathDetails: { ...prev.pathDetails, enrolledInPath: false },
      }));
      setEnrollmentModalDismissed(true);
      setIsEnrolled(false);
      refetchEnrollments();
      if (pathType === "Quest") {
        refetchQuests();
      } else {
        refetchLevels();
      }

      toast.current?.show({
        severity: "success",
        summary: "Unenrolled",
        detail: `You have successfully unenrolled from this ${pathTypeName}.`,
        life: 3000,
      });
    } catch (_error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to unenroll from ${pathTypeName}. Please try again.`,
        life: 3000,
      });
    }
  };

  // Handler for when a lesson is clicked
  const handleLessonClick = lessonRoute => {
    // If user is not enrolled, show the enrollment prompt
    if (!isEnrolled) {
      setPendingLessonRoute(lessonRoute);
      setShowEnrollmentModal(true);
      return;
    }

    // User is enrolled, navigate directly
    navigateTo(lessonRoute);
  };

  const handleModalCancel = () => {
    // For reassigned paths, don't navigate — user must enroll first
    if (!isReassignedAndNotEnrolled && pendingLessonRoute) {
      navigateTo(pendingLessonRoute);
      setPendingLessonRoute(null);
    }
    setShowEnrollmentModal(false);
  };

  const handleModalEnroll = async () => {
    await handleEnroll();
    setShowEnrollmentModal(false);
  };

  if (noResultsFound) {
    return (
      <div className="my-8 flex flex-col gap-4">
        <h1 className="atlas-h2">Path Not Found</h1>
        <p className="body-regular">We couldn't find the Path that you were looking for.</p>
        <div className="mt-2">
          <Button
            label={`Back to ${INTERNAL_ROUTES.Learn.name}`}
            onClick={() => navigateTo(INTERNAL_ROUTES.Learn.route)}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-test-id="learn-path-page">
      <PageBreadcrumb />
      <Toast ref={toast} />

      {!isEmpty(pathData.pathDetails) && (
        <PathDetails
          {...pathData.pathDetails}
          enrollInPath={handleEnroll}
          unEnrollInPath={handleUnenroll}
          isEnrolled={isEnrolled}
          pathType={pathType}
          currentLevelEnrollment={currentLevelEnrollment}
          hasOutstandingStrictAssignment={hasOutstandingStrictAssignment}
          isInactivePath={isInactivePath}
          isLevelLocked={isLevelLocked}
          disabledEnrollButton={disabledEnrollButton}
        />
      )}

      {pathData.remainingLessons.length > 0 && (
        <LessonsContainer
          lessons={pathData.remainingLessons}
          title="Lessons to do"
          inPath={pathType === "Path"}
          onLessonClick={handleLessonClick}
          disabled={disableLessons}
        />
      )}

      {pathData.completedLessons.length > 0 && (
        <LessonsContainer
          lessons={pathData.completedLessons}
          title="Completed Lessons"
          onLessonClick={handleLessonClick}
          disabled={disableLessons}
        />
      )}

      <EnrollmentPromptModal
        visible={showEnrollmentModal && !enrollmentModalDismissed && !disabledEnrollButton}
        onHide={handleModalCancel}
        onEnroll={handleModalEnroll}
        pathName={pathData.pathDetails?.title}
        isLoading={isEnrolling}
        currentLevelEnrollment={currentLevelEnrollment}
        title={pathData.pathDetails?.title}
        reassigned={isReassignedAndNotEnrolled}
      />
    </div>
  );
};

export default Path;
